import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useServiceQualification } from '@hooks/useServiceQualification/useServiceQualification';
import { useEmptyCart } from '@hooks/useEmptyCart';
import { useResumeDetection } from '@hooks/useResumeDetection';
import { useStartNewOrder } from '@hooks/useStartNewOrder';
import NovusAddressIcon from '@assets/icons/novusAddress.svg?component';
import type { AddressFormState, NormalizedAddress } from '@components/AddressCapture/AddressCapture.types';
import AddressForm from '@components/AddressCapture/AddressForm';
import ImageBanner from '@components/modals/CheckAvailabilityDialog/ImageBanner';
import InProgressOrderModal from '@components/modals/InProgressOrderModal/InProgressOrderModal';
import type { QualificationRouteResult } from '@t/serviceQualification.types';
import { LeadCaptureReason } from '@t/serviceQualification.types';
import { ROUTES } from '@constants/routes';
import { useSessionContext } from '@contexts/SessionContext';
import DialogHeader from '@shared/components/overlays/DialogHeader/DialogHeader';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';

export type DialogView ='change_address_warning' | 'address_capture' | 'resume_shopping';

export type DialogCloseReason = 'backdropClick' | 'escapeKeyDown';

interface CheckAvailabilityDialogProps {
  open: boolean;
  onClose: (event?: unknown, reason?: DialogCloseReason) => void;
  onQualificationSuccess: () => void;
  onNavigateToLeadCapture: () => void;
  /** When set, the dialog starts in this view instead of address_capture */
  initialView?: DialogView;
}

const CheckAvailabilityDialog = ({
  open,
  onClose,
  onQualificationSuccess,
  onNavigateToLeadCapture,
  initialView = 'address_capture',
}: CheckAvailabilityDialogProps) => {
  const navigate = useNavigate();

  const { sessionId, ensureSession } = useSessionContext();
  const {
    capturedAddress,
    resumeDetectionFailed,
    leadCaptureStatus,
    hasActiveWorkOrder: storeHasActiveWorkOrder,
    setQualificationFlow,
  } = useQualificationFlowStore(
    useShallow((s) => ({
      capturedAddress: s.capturedAddress,
      resumeDetectionFailed: s.resumeDetectionFailed,
      leadCaptureStatus: s.leadCaptureStatus,
      hasActiveWorkOrder: s.hasActiveWorkOrder,
      setQualificationFlow: s.setQualificationFlow,
    }))
  );

  const { resumeRoute, hasActiveWorkOrder, checkForResume } = useResumeDetection(sessionId);
  const { emptyCart } = useEmptyCart(sessionId ?? '');
  const { startNewOrder, isStartingNew } = useStartNewOrder();

  const initialAddressState = (): AddressFormState => {
    if (storeHasActiveWorkOrder) return 'active_account';
    if (resumeDetectionFailed) return 'system_error';
    return 'idle';
  };
  const [addressState, setAddressState] = useState<AddressFormState>(initialAddressState);
  const [dialogView, setDialogView] = useState<DialogView>(initialView);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [forceUnitPrompt, setForceUnitPrompt] = useState(false);

  useEffect(() => {
    if (open) {
      setDialogView(initialView);
      setAddressState(initialAddressState());
    }
  }, [open, initialView]);

  const handleRoute = useCallback(
    async (route: QualificationRouteResult) => {
      switch (route.destination) {
        case 'PRODUCT_LANDING': {
          const hasResume = await checkForResume();
          if (hasActiveWorkOrder) {
            setAddressState('active_account');
            return;
          }
          if (hasResume) {
            setDialogView('resume_shopping');
            return;
          }
          onQualificationSuccess();
          break;
        }
        case 'LEAD_CAPTURE':
          onNavigateToLeadCapture();
          break;
        case 'INLINE_ERROR':
          setAddressState('system_error');
          break;
        case 'REQUEST_UNIT':
          setForceUnitPrompt(true);
          setAddressState('idle');
          return;
      }
    },
    [checkForResume, onQualificationSuccess, onNavigateToLeadCapture]
  );

  const { qualify, isPending: isQualifying } = useServiceQualification({
    onRoute: handleRoute,
    onError: (error) => {
      if (error.name === '13') {
        setAddressState('active_account');
        return;
      }
      setQualificationFlow((prev) => ({
        ...prev,
        qualificationResult: null,
        leadCaptureReason: LeadCaptureReason.SYSTEM_ERROR,
      }));
      setAddressState('system_error');
    },
  });

  const handleCheckServiceability = useCallback(
    async (normalized: NormalizedAddress) => {
      if (addressState === 'active_account') return;

      setAddressState('idle');
      setForceUnitPrompt(false);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { formattedAddress: _f, displayAddress: _d, ...address } = normalized;
      setQualificationFlow((prev) => ({ ...prev, capturedAddress: normalized }));

      try {
        setIsCreatingSession(true);
        const sid = await ensureSession();
        setIsCreatingSession(false);

        if (dialogView === 'change_address_warning') {
          await emptyCart();
        }

        qualify(sid, { address }, { forceFresh: dialogView === 'change_address_warning' });
      } catch {
        setIsCreatingSession(false);
        setAddressState('system_error');
      }
    },
    [addressState, dialogView, ensureSession, emptyCart, qualify, setQualificationFlow]
  );

  const handleClose = (e?: unknown, reason?: DialogCloseReason) => {
    if (addressState === 'active_account' || leadCaptureStatus === 'dismissed') return;
    setAddressState('idle');
    setForceUnitPrompt(false);
    setDialogView(initialView);
    onClose?.(e, reason);
  };

  const handleContinuePreviousOrder = () => {
    if (resumeRoute === ROUTES.CHECKOUT) {
      navigate(ROUTES.CHECKOUT);
    } else {
      onQualificationSuccess();
    }
    onClose();
  };

  const handleStartNewOrder = async () => {
    try {
      await emptyCart();
      await startNewOrder();
      onQualificationSuccess();
    } catch {
      setAddressState('system_error');
    }
  };

  const handleInProgressClose = () => {
    setDialogView('address_capture');
  };

  const isAddressFormVisible = dialogView === 'address_capture' || dialogView === 'change_address_warning';
  const isCartReadyVisible = dialogView === 'resume_shopping';
  const isPending = isCreatingSession || isQualifying;

  return (
    <>
      <Dialog
        open={open && isAddressFormVisible}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        data-backdrop-variant="blurred"
        className="check-availability-dialog"
      >
        <DialogHeader onClose={handleClose} title="Check Availability" variant="title.dialogCompact" />
        <Stack className="check-availability-content">
          <ImageBanner icon={NovusAddressIcon} />
          {dialogView === 'change_address_warning' && (
            <Paper variant="warningCallout" elevation={0}>
              <Typography variant="body.md" color="text.secondary">
                <Typography variant="label.button" className="change-address-warning-text">
                  Warning:
                </Typography>{' '}
                Changing your service address will reset your order. Any items in your cart will be removed. You will be
                able to select new products after updating your address.{' '}
              </Typography>
            </Paper>
          )}
        </Stack>
        <DialogContent>
          <AddressForm
            onSubmit={handleCheckServiceability}
            onContactUs={() => onNavigateToLeadCapture()}
            onAddressChange={() => {
              setAddressState('idle');
              setForceUnitPrompt(false);
            }}
            // Deliberately NOT routed to lead capture: its address-not-found path
            // would tell Novus the address is unserviceable when the truth is the
            // lookup was throttled, filling the lead pipeline with false gaps.
            onLookupUnavailable={() => setAddressState('lookup_unavailable')}
            isLoading={isPending}
            addressState={addressState}
            defaultAddress={capturedAddress ?? undefined}
            forceUnitPrompt={forceUnitPrompt}
          />
        </DialogContent>
      </Dialog>

      <InProgressOrderModal
        open={open && isCartReadyVisible}
        onClose={handleInProgressClose}
        onContinuePreviousOrder={handleContinuePreviousOrder}
        onStartNewOrder={handleStartNewOrder}
        isLoading={isStartingNew}
      />
    </>
  );
};

export default CheckAvailabilityDialog;
